import { lazy, useEffect } from "react";
import { Navigate, useNavigate, useParams, useRoutes } from "react-router-dom";

// layouts
import DashboardLayout from "./layouts/dashboard";
import SimpleLayout from "./layouts/simple";
import RequireRole from "./guards/RequireRole";

//
import { verifyTokenExpiry } from "./utils/verifyToken";

import { DeliveryRequestProvider } from "./pages/deliver_request/context/deliveryRequestContext";

import Page404 from "./pages/404/Page404";
import LoginPage from "./pages/login/LoginPage";
const DriverGpsPage = lazy(() => import("./pages/driver_gps/index"));

const DeliverRequestPage = lazy(() => import("./pages/deliver_request/delivery_request"));
const JipangeConfigurationPage = lazy(() => import("./pages/jipange"));
const ResetPasswordPage = lazy(() => import("./pages/reset_password/ResetPasswordPage"));
const PromoNotificationPage = lazy(() => import("./pages/notification/PromoNotificationPage"));

//Users
const UsersPage = lazy(() => import("./pages/users/index"));
const UsersList = lazy(() => import("./pages/users/list/userList"));
const UserDetails = lazy(() => import("./pages/users/details/userDetails"));

//Partners
const PartnerTabPage = lazy(() => import("./pages/partners/partnerTab"));
const PartnersPage = lazy(() => import("./pages/partners/index"));
const AddPartner = lazy(() => import("./pages/partners/add/addPartner"));
const EditPartner = lazy(() => import("./pages/partners/edit/editPartner"));
const PartnerAssociations = lazy(() => import("./pages/partners/associations/index"));
//Homepage sections
const HomepagePage = lazy(() => import("./pages/homepage/index"));
const HomepageTabPage = lazy(() => import("./pages/homepage/homepageTab"));
const EditDeliveryRequestConfig = lazy(
  () => import("./pages/partners/deliveryRequests/edit/editDeliveryRequestConfig")
);

//Preloved
const PrelovedTabPage = lazy(() => import("./pages/preloved/prelovedTab"));
const PrelovedPage = lazy(() => import("./pages/preloved/index"));
const PrelovedListing = lazy(() => import("./pages/preloved/list/prelovedList"));
const PrelovedItemPage = lazy(() => import("./pages/preloved/details/index"));
const MarketplaceRequestDetailsPage = lazy(
  () => import("./pages/preloved/marketplace/details/index")
);
const PrelovedSellerDetailsPage = lazy(() => import("./pages/preloved/sellers/details/index"));

// PrelovedConfig
const PrelovedConfig = lazy(() => import("./pages/preloved_config/index"));

//Brands
const BrandsPage = lazy(() => import("./pages/brands/index"));
const BrandsList = lazy(() => import("./pages/brands/list/brandList"));
const EditBrand = lazy(() => import("./pages/brands/edit/editBrand"));
const AddBrand = lazy(() => import("./pages/brands/add/addBrand"));

//Delivery
const DeliveryPage = lazy(() => import("./pages/delivery/index"));
const DeliveryDashboardTab = lazy(() => import("./pages/delivery/dashboard/deliveryDashboardTab"));
const DeliveryLiveMapTab = lazy(() => import("./pages/delivery/live_map/deliveryLiveMapTab"));
const DeliveryRoutesTab = lazy(() => import("./pages/delivery/batches/deliveryBatchesTab"));
const AddRoute = lazy(() => import("./pages/delivery/batches/add/addBatch"));
const RouteDetails = lazy(() => import("./pages/delivery/batches/details/batchDetails"));
const DeliveryDriversTab = lazy(() => import("./pages/delivery/drivers/deliveryDriversTab"));
const AddDriver = lazy(() => import("./pages/delivery/drivers/add/addDriver"));
const EditDriver = lazy(() => import("./pages/delivery/drivers/edit/editDriver"));
const DeliveryVehiclesTab = lazy(() => import("./pages/delivery/vehicles/deliveryVehiclesTab"));
const AddVehicle = lazy(() => import("./pages/delivery/vehicles/add/addVehicle"));
const EditVehicle = lazy(() => import("./pages/delivery/vehicles/edit/editVehicle"));
const DeliveryDepotsTab = lazy(() => import("./pages/delivery/depots/deliveryDepotsTab"));
const AddDepot = lazy(() => import("./pages/delivery/depots/add/addDepot"));
const EditDepot = lazy(() => import("./pages/delivery/depots/edit/editDepot"));
const DeliveryZonesTab = lazy(() => import("./pages/delivery/zones/deliveryZonesTab"));
const HeadingBoardTab = lazy(() => import("./pages/delivery/heading_board/HeadingBoardTab"));

//Products
const ProductPage = lazy(() => import("./pages/product/index"));
const ProductTabPage = lazy(() => import("./pages/product/productTab"));
const AddProduct = lazy(() => import("./pages/product/add/addProduct"));
const EditProduct = lazy(() => import("./pages/product/edit/editProduct"));
const AddVariant = lazy(() => import("./pages/product/variant/addVariant"));
const ProductSortManager = lazy(() => import("./pages/category/product_sort"));

//Pamojas
const PamojasPage = lazy(() => import("./pages/pamojas/index"));
const PamojasTabPage = lazy(() => import("./pages/pamojas/pamojasTab"));

//Orders
const OrderTabPage = lazy(() => import("./pages/orders/orderTab"));
const OrdersPage = lazy(() => import("./pages/orders/index"));
const OrderDetailsPage = lazy(() => import("./pages/orders/details/orderDetails"));
const AddOrder = lazy(() => import("./pages/orders/add/addOrder"));
const BulkEstimate = lazy(() => import("./pages/orders/bulk-estimate/bulkEstimate"));
const OrderIssuanceTab = lazy(() => import("./pages/orders/issuance/orderIssuanceTab"));
const SourcingListPage = lazy(() => import("./pages/orders/sourcing/sourcingList"));

//Promos
const PromosPage = lazy(() => import("./pages/promos/index"));
const PromoList = lazy(() => import("./pages/promos/list/promoList"));
const EditPromo = lazy(() => import("./pages/promos/edit/editPromo"));
const AddPromo = lazy(() => import("./pages/promos/add/addPromo"));

//Audit
const AuditPage = lazy(() => import("./pages/audit/index"));
const AuditList = lazy(() => import("./pages/audit/list/auditList"));
const ServiceLogList = lazy(() => import("./pages/service_logs/list/serviceLogList"));

//Message Center
const MessageCenterPage = lazy(() => import("./pages/message_center/index"));
const MessageList = lazy(() => import("./pages/message_center/list/messageList"));
const MessageDetails = lazy(() => import("./pages/message_center/details/messageDetails"));

// AppConfig
const AppConfig = lazy(() => import("./pages/app_config/index"));
const AppConfigurations = lazy(() => import("./pages/app_config/config/index"));
const EditAppBannerConfiguration = lazy(() => import("./pages/app_config/edit/index"));
const AddAppBannerConfiguration = lazy(() => import("./pages/app_config/add/index"));
const ManageAppConfigurationImages = lazy(() => import("./pages/app_config/manage_images/index"));

// BannerConfig (app)
const BannerConfig = lazy(() => import("./pages/app_config/bannerConfig/index"));
const AddBannerConfig = lazy(() => import("./pages/app_config/bannerConfig/add/index"));
const EditBannerConfig = lazy(() => import("./pages/app_config/bannerConfig/edit/index"));

// WebConfig
const WebConfig = lazy(() => import("./pages/web_config/index"));
const WebConfigurations = lazy(() => import("./pages/web_config/config/index"));
const EditWebBannerConfiguration = lazy(() => import("./pages/web_config/edit/index"));
const AddWebBannerConfiguration = lazy(() => import("./pages/web_config/add/index"));
const ManageWebConfigurationImages = lazy(() => import("./pages/web_config/manage_images/index"));

//Reports
const ReportPage = lazy(() => import("./pages/reports/index"));
const ReportsDashbaord = lazy(() => import("./pages/reports/reportsPage"));

//Finance
const FinancePage = lazy(() => import("./pages/finance/index"));
const MpesaTransactionsPage = lazy(
  () => import("./pages/finance/mpesaTransactions/mpesaTransactionsTable")
);
const RefundsPage = lazy(() => import("./pages/finance/refunds/refundsTable"));
const UnpaidOrdersPage = lazy(() => import("./pages/finance/unpaidOrders/unpaidOrdersTable"));

//GlobalConfig
const GlobalConfig = lazy(() => import("./pages/global_config/index"));
const GlobalConfigTabPage = lazy(() => import("./pages/global_config/globalConfigTab"));
const EditTushopCorridor = lazy(
  () => import("./pages/global_config/deliveryConfig/edit/editTushopCorridor")
);

//Utilities
const UtilitiesPage = lazy(() => import("./pages/utilities/index"));
const UtilitiesList = lazy(() => import("./pages/utilities/list/utilitiesList"));
const ProductImageMigration = lazy(() => import("./pages/utilities/image_migration/index"));
const RecommendationEngine = lazy(() => import("./pages/utilities/recommendation_engine/index"));

// ----------------------------------------------------------------------

const WebConfigAddRedirect = () => {
  const { config } = useParams();
  return <Navigate to={`/dashboard/web-config/${config}/add`} replace />;
};

const WebConfigEditRedirect = () => {
  const { config, imageName } = useParams();
  return <Navigate to={`/dashboard/web-config/${config}/edit/${imageName}`} replace />;
};

export default function Router() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // Check token status
  useEffect(() => {
    if (!token) {
      navigate("/login", { replace: true });
    } else {
      verifyTokenExpiry(token, navigate);
    }
  }, [token, navigate]);

  const routes = useRoutes([
    {
      path: "/dashboard",
      element: (
        <RequireRole allow="non-driver">
          <DashboardLayout />
        </RequireRole>
      ),
      children: [
        //Products
        { index: true, element: <Navigate to="products" replace /> },
        {
          path: "products",
          element: <ProductPage />,
          children: [
            { index: true, element: <ProductTabPage /> },
            { path: "add", element: <AddProduct /> },
            { path: "variant/add", element: <AddVariant /> },
            { path: "edit/:productVariantId", element: <EditProduct /> },
            { path: "sort/manager", element: <ProductSortManager /> }
          ]
        },

        //Preloved configuration
        { path: "/dashboard/preloved-config", element: <PrelovedConfig /> },

        //Global configurations
        {
          path: "/dashboard/global-config",
          element: <GlobalConfig />,
          children: [
            { index: true, element: <GlobalConfigTabPage /> },
            { path: "delivery/add", element: <EditTushopCorridor /> },
            { path: "delivery/:corridorZoneId", element: <EditTushopCorridor /> }
          ]
        },

        //Utilities
        {
          path: "/dashboard/utilities",
          element: <UtilitiesPage />,
          children: [
            { path: "", element: <UtilitiesList /> },
            { path: "product-image-migration", element: <ProductImageMigration /> },
            { path: "recommendation-engine", element: <RecommendationEngine /> }
          ]
        },

        //Firebase configurations
        //Web configurations
        { path: "/dashboard/web-config", element: <WebConfig /> },
        { path: "/dashboard/web-config/:config", element: <WebConfigurations /> },
        {
          path: "/dashboard/firebase-config-web/:config/manage-images",
          element: <ManageWebConfigurationImages />
        },
        {
          path: "/dashboard/firebase-config-web/:config/add",
          element: <WebConfigAddRedirect />
        },
        {
          path: "/dashboard/web-config/:config/add",
          element: <AddWebBannerConfiguration />
        },
        {
          path: "/dashboard/firebase-config-web/:config/edit/:imageName",
          element: <WebConfigEditRedirect />
        },
        {
          path: "/dashboard/web-config/:config/edit/:imageName",
          element: <EditWebBannerConfiguration />
        },

        //App configurations
        { path: "/dashboard/app-config", element: <AppConfig /> },
        { path: "/dashboard/app-config/bannerConfig", element: <BannerConfig /> },
        {
          path: "/dashboard/app-config/bannerConfig/add",
          element: <AddBannerConfig />
        },
        {
          path: "/dashboard/app-config/bannerConfig/edit/:imageName",
          element: <EditBannerConfig />
        },
        { path: "/dashboard/app-config/:config", element: <AppConfigurations /> },
        {
          path: "/dashboard/firebase-config-app/:config/manage-images",
          element: <ManageAppConfigurationImages />
        },
        {
          path: "/dashboard/firebase-config-app/:config/add",
          element: <AddAppBannerConfiguration />
        },
        {
          path: "/dashboard/firebase-config-app/:config/edit/:imageName",
          element: <EditAppBannerConfiguration />
        },

        //Bramds
        {
          path: "/dashboard/brands",
          element: <BrandsPage />,
          children: [
            { path: "", element: <BrandsList /> },
            { path: "edit/:brandId", element: <EditBrand /> },
            { path: "add", element: <AddBrand /> }
          ]
        },

        //Delivery
        {
          path: "/dashboard/delivery",
          element: <DeliveryPage />,
          children: [
            { index: true, element: <Navigate to="/dashboard/delivery/dashboard" /> },
            { path: "dashboard", element: <DeliveryDashboardTab /> },
            { path: "live-map", element: <DeliveryLiveMapTab /> },
            { path: "batches", element: <DeliveryRoutesTab /> },
            { path: "batches/add", element: <AddRoute /> },
            { path: "batches/details/:batchId", element: <RouteDetails /> },
            { path: "heading-board", element: <HeadingBoardTab /> },
            { path: "drivers", element: <DeliveryDriversTab /> },
            { path: "drivers/add", element: <AddDriver /> },
            { path: "drivers/edit/:driverId", element: <EditDriver /> },
            { path: "vehicles", element: <DeliveryVehiclesTab /> },
            { path: "vehicles/add", element: <AddVehicle /> },
            { path: "vehicles/edit/:vehicleId", element: <EditVehicle /> },
            { path: "depots", element: <DeliveryDepotsTab /> },
            { path: "depots/add", element: <AddDepot /> },
            { path: "depots/edit/:depotId", element: <EditDepot /> },
            { path: "zones", element: <DeliveryZonesTab /> }
          ]
        },

        //Users
        {
          path: "/dashboard/users",
          element: <UsersPage />,
          children: [
            { path: "", element: <UsersList /> },
            { path: "details/:userId", element: <UserDetails /> }
          ]
        },

        //Orders
        {
          path: "/dashboard/orders",
          element: <OrdersPage />,
          children: [
            { index: true, element: <OrderTabPage /> },
            { path: "add", element: <AddOrder /> },
            { path: "bulk-estimate", element: <BulkEstimate /> },
            { path: "issuance", element: <OrderIssuanceTab /> },
            { path: "details/:orderId", element: <OrderDetailsPage /> },
            { path: "sourcing-list", element: <SourcingListPage /> }
          ]
        },
        {
          path: "orders/delivery-request",
          element: (
            <DeliveryRequestProvider>
              <DeliverRequestPage />
            </DeliveryRequestProvider>
          )
        },

        //Promos
        {
          path: "/dashboard/promos",
          element: <PromosPage />,
          children: [
            { path: "", element: <PromoList /> },
            { path: "edit/:promoCodeId", element: <EditPromo /> },
            { path: "add", element: <AddPromo /> }
          ]
        },

        //Partners
        {
          path: "/dashboard/partners",
          element: <PartnersPage />,
          children: [
            { index: true, element: <PartnerTabPage /> },
            { path: "edit/:partnerId/associations", element: <PartnerAssociations /> },
            { path: "edit/:partnerId", element: <EditPartner /> },
            { path: "add", element: <AddPartner /> },
            { path: "delivery-requests/edit/:partnerId", element: <EditDeliveryRequestConfig /> }
          ]
        },

        //Homepage sections — the index for every admin-curated homepage section
        {
          path: "/dashboard/homepage",
          element: <HomepagePage />,
          children: [{ index: true, element: <HomepageTabPage /> }]
        },

        //Preloved approvals
        {
          path: "/dashboard/preloved",
          element: <PrelovedPage />,
          children: [
            { index: true, element: <PrelovedTabPage /> },
            { path: "", element: <PrelovedListing /> },
            { path: "details/:prelovedListingId", element: <PrelovedItemPage /> },
            {
              path: "marketplace/:prelovedMarketplaceRequestId/listing/:prelovedListingId",
              element: <MarketplaceRequestDetailsPage />
            },
            { path: "sellers/:sellerId", element: <PrelovedSellerDetailsPage /> }
          ]
        },

        //Message Center
        {
          path: "/dashboard/message-center",
          element: <MessageCenterPage />,
          children: [
            { path: "", element: <MessageList /> },
            { path: "details/:messageId", element: <MessageDetails /> }
          ]
        },

        //Reports
        {
          path: "/dashboard/reports",
          element: <ReportPage />,
          children: [{ path: "", element: <ReportsDashbaord /> }]
        },

        //Finance
        {
          path: "/dashboard/finance",
          element: <FinancePage />,
          children: [
            { path: "mpesa-transactions", element: <MpesaTransactionsPage /> },
            { path: "refunds", element: <RefundsPage /> },
            { path: "unpaid-orders", element: <UnpaidOrdersPage /> }
          ]
        }
      ]
    },
    {
      path: "/dashboard/okoa",
      element: (
        <RequireRole allow="non-driver">
          <DashboardLayout />
        </RequireRole>
      ),
      children: [
        {
          path: "pamoja",
          element: <PamojasPage />,
          children: [{ index: true, element: <PamojasTabPage /> }]
        },

        { path: "jipange", element: <JipangeConfigurationPage /> }
      ]
    },
    {
      path: "/dashboard/audit",
      element: (
        <RequireRole allow="non-driver">
          <DashboardLayout />
        </RequireRole>
      ),
      children: [
        { element: <Navigate to="/dashboard/settings" />, index: true },
        //Audit
        {
          path: "/dashboard/audit/logs",
          element: <AuditPage />,
          children: [
            { path: "", element: <AuditList /> },
            { path: "service-logs", element: <ServiceLogList /> }
          ]
        },
        { path: "notifications", element: <PromoNotificationPage /> }
      ]
    },

    //Login
    {
      path: "login",
      element: <LoginPage />
    },

    //Driver - restricted, no DashboardLayout/sidebar chrome
    {
      path: "/dashboard/driver",
      element: (
        <RequireRole allow="driver">
          <DriverGpsPage />
        </RequireRole>
      )
    },

    //Rdset Password
    {
      path: "reset-password",
      element: <ResetPasswordPage />
    },
    {
      element: <SimpleLayout />,
      children: [
        { element: <Navigate to="/dashboard/products" />, index: true },
        { path: "404", element: <Page404 /> },
        { path: "*", element: <Navigate to="/404" /> }
      ]
    },

    //Redirect all unmatched routes to 404
    {
      path: "*",
      element: <Navigate to="/404" replace />
    }
  ]);

  return routes;
}
